// Link Preview Service
// Date: January 17, 2025
// Purpose: Fetch Open Graph and meta tag data from external URLs for previews

import api from './api';

export interface LinkPreview {
  title?: string;
  description?: string;
  image?: string;
  siteName?: string;
  url?: string;
  favicon?: string;
}

/**
 * Fetches link preview metadata from an external URL
 * Uses backend proxy to avoid CORS issues
 */
export const fetchLinkPreview = async (url: string): Promise<LinkPreview> => {
  try {
    // Use backend API to fetch link preview (avoids CORS issues)
    const response = await api.get(`/resources/link-preview`, {
      params: { url }
    });

    if (response.data.success && response.data.data) {
      return {
        title: response.data.data.title,
        description: response.data.data.description,
        image: response.data.data.image,
        siteName: response.data.data.siteName || response.data.data.site_name,
        url: response.data.data.url || url,
        favicon: response.data.data.favicon,
      };
    }

    // Fallback: return basic info
    return {
      url,
      title: new URL(url).hostname,
    };
  } catch (error) {
    console.warn('Link preview fetch failed:', error);
    // Return fallback preview
    try {
      const urlObj = new URL(url);
      return {
        url,
        title: urlObj.hostname,
        siteName: urlObj.hostname.replace('www.', ''),
      };
    } catch {
      return {
        url,
        title: 'External Link',
      };
    }
  }
};

/**
 * Validates if a URL is valid and external
 */
export const isValidExternalUrl = (url: string): boolean => {
  try {
    const urlObj = new URL(url);
    return urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
  } catch {
    return false;
  }
};

